import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ILanguageEntityAttributes extends IModelAttributes {
	name: string;
	code: string;
	locale: string;
	translations: string;
	isDisabled: boolean;

	configGeneral?: Models.ConfigGeneralEntity | Models.IConfigGeneralEntityAttributes;
}
@entity('LanguageEntity', 'Language')
export default class LanguageEntity extends Model implements ILanguageEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The name of the language (e.g. English).
	 */
	@observable
	@attribute<LanguageEntity, string>()
	public name: string;

	/**
	 * Stores the ISO code for the language (e.g. en).
	 */
	@observable
	@attribute<LanguageEntity, string>()
	public code: string;

	/**
	 * Stores the full locale code (e.g. en-AU).
	 */
	@observable
	@attribute<LanguageEntity, string>()
	public locale: string;

	/**
	 * The translations of the text around the application.
	 */
	@observable
	@attribute<LanguageEntity, string>()
	public translations: string;

	/**
	 * Whether or not the language is disabled on the frontend of the application.
	 */
	@observable
	@attribute<LanguageEntity, boolean>()
	public isDisabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public configGeneral?: Models.ConfigGeneralEntity;

	constructor(attributes?: Partial<ILanguageEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ILanguageEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.code !== undefined) {
				this.code = attributes.code;
			}
			if (attributes.locale !== undefined) {
				this.locale = attributes.locale;
			}
			if (attributes.translations !== undefined) {
				this.translations = attributes.translations;
			}
			if (attributes.isDisabled !== undefined) {
				this.isDisabled = attributes.isDisabled;
			}
			if (attributes.configGeneral !== undefined) {
				if (attributes.configGeneral === null) {
					this.configGeneral = attributes.configGeneral;
				} else if (attributes.configGeneral instanceof Models.ConfigGeneralEntity) {
					this.configGeneral = attributes.configGeneral;
				} else {
					this.configGeneral = new Models.ConfigGeneralEntity(attributes.configGeneral);
				}
			}
		}
	}
}
